/* eslint-disable import/newline-after-import, import/first */

//region 1. Platform Libraries
import express from 'express';
import moment from 'moment';
import MongoClient from 'mongodb';
import os from 'os';
const requireAll = require('require-all');
import winston from 'winston';
import 'winston-daily-rotate-file';
import 'winston-loggly-bulk';
//endregion

//region 2. Project Libraries
import LogLevel from './LogLevel';
//endregion

//region K. Constants
const context = {
    heartbeatInterval: null,
    loadHistoryMaxDays: null,
    logger: null,
    routers: null,
};
//endregion

//region B. Business Helpers
const timestamp = () => moment().format('YYYY-MM-DDTHH:mm:ss.SSSZ');

const formatter = (options) => {
    const timestampPart = `${options.timestamp()} `;
    const levelPart = `${options.level.toUpperCase()}: `;
    const tagPart = options.meta.tags ? `${options.meta.tags}> ` : '';
    const messagePart = options.message || '';
    return `${timestampPart}${levelPart}${tagPart}${messagePart}`;
};

context.logger = (function setupDefaultLogger() {
    const transports = [
        new winston.transports.Console({
            formatter,
            timestamp,
        }),
        new winston.transports.DailyRotateFile({
            datePattern: '.yyyy-MM-dd',
            filename: `${process.cwd()}/data-api.log`,
            formatter,
            maxFiles: 7,
            timestamp,
        }),
    ];
    return new winston.Logger({ transports });
}());

function setupLogger(logLevel, logglySubdomain, logglyToken) {
    this.logLevel = LogLevel[logLevel && logLevel.toUpperCase()] || LogLevel.DEBUG;
    const transports = [
        new winston.transports.Console({
            formatter,
            timestamp,
        }),
        new winston.transports.DailyRotateFile({
            datePattern: '.yyyy-MM-dd',
            filename: `${process.cwd()}/data-api.log`,
            formatter,
            json: false,
            maxFiles: 7,
            timestamp,
        }),
    ];
    if (logglySubdomain && logglySubdomain !== 'null'
        && logglyToken && logglyToken !== 'null') {
        const tags = [
            'data-api',
            os.hostname,
        ];
        if (this.environment) {
            tags.push(this.environment);
        }
        transports.push(new winston.transports.Loggly({
            formatter,
            json: true,
            level: this.logLevel.name.toLowerCase(),
            subdomain: logglySubdomain,
            tags,
            timestamp,
            token: logglyToken,
        }));
    }
    context.logger.configure({
        level: this.logLevel.name.toLowerCase(),
        transports,
    });
}
//endregion

//region E. Exports
/**
 * Class representing a Data-API
 */
export default class DataAPI {
    constructor() {
        this.expressApp = express();
    }

    /**
     * Initialize Data-API with options
     * @param {object}   [options]                              Options
     * @param {string}   [options.environment]                  Environment Tag
     * @param {number}   [options.heartbeatInterval=10]         Heartbeat Interval (s)
     * @param {string}   [options.logglySubdomain]              Loggly Subdomain
     * @param {string}   [options.logglyToken]                  Loggly Token
     * @param {string}   [options.logLevel=DEBUG]               Log Level
     * @param {string}   [options.mongoUrl=mongodb://localhost] MongoDB Connection URL
     * @param {number}   [options.port=15925]                   Data-API Port
     */
    initialize(options) {
        const opts = options || {};
        this.environment = opts.environment || '';
        setupLogger.bind(this)(opts.logLevel, opts.logglySubdomain, opts.logglyToken);
        const log = (lvl, msg) => context.logger.log(lvl, msg, { tags: 'initialize' });
        log('debug', 'Initializing....');
        if (opts.heartbeatInterval === 0) {
            log('error', `Invalid Heartbeat Interval ${opts.heartbeatInterval}`);
            return false;
        }
        if (opts.mongoUrl && opts.mongoUrl.substring(0, 10) !== 'mongodb://') {
            log('error', `Invalid MongoDB Connection URL ${opts.mongoUrl}`);
            return false;
        }
        if (opts.port < 1 || opts.port > 65535) {
            log('error', `Port Number Range Exceeded ${opts.port}`);
            return false;
        }
        context.heartbeatInterval = opts.heartbeatInterval || 10;
        context.loadHistoryMaxDays = opts.loadHistoryMaxDays || 7;
        this.mongoUrl = opts.mongoUrl || 'mongodb://localhost';
        this.port = opts.port || 15925;
        log('info', 'Initialized');
        return true;
    }

    start(callback) {
        const log = (lvl, msg) => context.logger.log(lvl, msg, { tags: 'start' });
        log('debug', 'Checking existing database connection....');
        if (this.database) {
            log('error', 'Data API has already been started. Start aborted.');
            return false;
        }
        log('debug', 'Connecting to database....');
        MongoClient.connect(this.mongoUrl, (connectErr, connectedDB) => {
            if (connectErr) {
                log('error', connectErr.message);
                return;
            }
            log('info', 'Database Connected');
            this.database = connectedDB;
            log('debug', 'Mounting routers....');
            context.routers = requireAll({
                dirname: `${__dirname}/routers`,
                filter: /^((?!spec).)+\.js$/,
                map(name, path) {
                    const filename = path.substring(
                        path.lastIndexOf('/') + 1,
                        path.lastIndexOf('.'),
                    );
                    return name.length === 1 ? filename : name;
                },
            });
            log('debug', Object.keys(context.routers));
            Object.keys(context.routers).forEach((router) => {
                context.routers[router].default({
                    app: this.expressApp,
                    database: this.database,
                    logger: context.logger,
                });
            });
            log('info', 'Routers Mounted');
            log('debug', 'Launching Data API....');
            this.expressServer = this.expressApp.listen(this.port, (listenErr) => {
                if (listenErr) {
                    log('error', listenErr.message);
                    return;
                }
                log('info', `Data API listening on port ${this.port}`);
                if (callback) {
                    callback();
                }
            });
        });
        return true;
    }

    stop(callback) {
        const log = (lvl, msg) => context.logger.log(lvl, msg, { tags: 'stop' });
        log('debug', 'Stopping Data API....');
        if (!this.expressServer) {
            log('info', 'Express Server Already Closed');
            if (callback) {
                callback();
            }
            return;
        }
        this.expressServer.close(() => {
            log('info', 'Express Server Closed');
            this.expressServer = null;
            if (!this.database) {
                if (callback) {
                    callback();
                }
                return;
            }
            this.database.close(() => {
                log('info', 'Database Closed');
                this.database = null;
                if (callback) {
                    callback();
                }
            });
        });
    }

    static get heartbeatInterval() {
        return context.heartbeatInterval;
    }
}

export const logger = context.logger;
//endregion
